import {useEffect, useState, useCallback} from "react";
import toast from "react-hot-toast";
import {useNavigate, useParams} from "react-router-dom";
import {fetchWithAuth} from "../libs/apiUtils";
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { 
  ClipboardDocumentCheckIcon, 
  CodeBracketIcon, 
  DocumentTextIcon, 
  ClockIcon,
  TagIcon
} from '@heroicons/react/24/outline';

interface TableDetail {
  id: string
  name: string
  description: string
  updatedAt: Date
}

interface Row {
  rowId: string | null
  rowName: string | null
  rowShownAtIndex: number | null
  values: Column[]
}

interface Column {
  columnId: string | null
  columnName: string | null
  columnShownAtIndex: number | null
  value: string
}

interface Metadata {
  id: string
  name: string
  value: string
}

export default function TableAPIInterface() {
  const { tableId } = useParams()

  const [tableDetail, setTableDetail] = useState<TableDetail | null>(null)

  const [rows, setRows] = useState<Row[]>([])
  const [columns, setColumns] = useState<Column[]>([])

  const [metadata, setMetadata] = useState<Metadata[]>([])

  // pagination stuff
  const [loading, setLoading] = useState<boolean>(true)

  const navigate = useNavigate()

  const loadTableContent = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/table-content/read`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Internal-Request': 'true'
        },
        body: JSON.stringify({ tableId }),
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        toast.error(data.message || 'Failed to load table content')
        return
      }

      const rawRows: Row[] = data.data?.rows || []
      const rawCols: any[] = data.data?.columns || []

      const columnMap: Record<string, Column> = {}

      rawCols.forEach((col: any) => {
        const colKey = col.columnId || col.columnName
        columnMap[colKey] = {
          columnId: col.columnId,
          columnName: col.columnName,
          columnShownAtIndex: col.columnShownAtIndex,
          value: ''
        }
      })

      rawRows.forEach((row) => {
        row.values?.forEach((val) => {
          const colKey = val.columnId || val.columnName
          if (colKey && !columnMap[colKey]) {
            columnMap[colKey] = {
              columnId: val.columnId ?? null,
              columnName: val.columnName || `Column ${Object.keys(columnMap).length + 1}`,
              columnShownAtIndex: val.columnShownAtIndex ?? Object.keys(columnMap).length,
              value: val.value
            }
          }
        })
      })

      const sortedColumns = Object.values(columnMap).sort((a, b) => 
        (a.columnShownAtIndex ?? 0) - (b.columnShownAtIndex ?? 0)
      )

      const sortedRows = Object.values(rawRows).sort((a, b) => 
        (a.rowShownAtIndex ?? 0) - (b.rowShownAtIndex ?? 0)
      )
      
      setColumns(sortedColumns)
      setRows(sortedRows)
    } catch (error) {
      console.error(error)
      toast.error('Network error while loading table content')
    }
  }, [tableId])

  const loadTableMetadata = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/table-metadatas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tableId }),
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        toast.error(data.message || 'Failed to load table metadata')
        return
      }

      setMetadata(data.data)
    } catch (error) {
      console.error(error)
      toast.error('Network error while loading table metadata')
    }
  }, [tableId])

  useEffect(() => {
    async function validateTable() {
      try {
        setLoading(true)
        const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/table-detail`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tableId }),
        })

        if (!res.ok) {
          throw new Error('Invalid table')
        }

        const data = await res.json()
        setTableDetail({
          id: data.data.id as string,
          name: data.data.name as string,
          description: data.data.description as string,
          updatedAt: new Date(data.data.updatedAt)
        })
      } catch (err) {
        toast.error('Table not found')
        navigate('/data/group')
      } finally {
        setLoading(false)
      }
    }

    if (tableId) validateTable()
  }, [tableId, navigate])

  useEffect(() => {
    if (tableId) {
      loadTableContent()
      loadTableMetadata()
    }
  }, [tableId, loadTableContent, loadTableMetadata])

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Disalin ke papan klip');
  };

  if (loading) {
    return (
      <div className="p-20 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-500 font-medium">Memuat dokumentasi API...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8 text-gray-900">
      <div className="flex items-center gap-4">
        <button 
          onClick={() => navigate(-1)} 
          className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-600"
          title="Kembali"
        >
          <ArrowLeftIcon className="size-6" />
        </button>
        <div>
          <h2 className="text-3xl font-extrabold tracking-tight">Dokumentasi API</h2>
          <p className="mt-1 text-sm text-gray-500">Gunakan endpoint di bawah ini untuk mengakses data tabel secara terprogram.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Details & Metadata */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-2xl p-6 shadow-xl shadow-gray-100 border border-gray-100">
            <h3 className="text-lg font-bold flex items-center gap-2 mb-4">
              <DocumentTextIcon className="size-5 text-blue-600" />
              Detail Tabel
            </h3>
            <div className="space-y-4 text-sm">
              <div>
                <label className="block text-gray-500 font-medium mb-1">Nama Tabel</label>
                <div className="font-semibold text-gray-900">{tableDetail?.name}</div>
              </div>
              <div>
                <label className="block text-gray-500 font-medium mb-1">ID Tabel</label>
                <div className="bg-gray-50 p-2 rounded-lg font-mono text-xs text-gray-600 flex items-center justify-between">
                  {tableDetail?.id}
                  <button onClick={() => copyToClipboard(tableDetail?.id || '')} className="text-blue-600 hover:text-blue-800">
                    <ClipboardDocumentCheckIcon className="size-4" />
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-gray-500 font-medium mb-1 flex items-center gap-1">
                  <ClockIcon className="size-4" /> Terakhir Diperbarui
                </label>
                <div className="text-gray-900">
                  {tableDetail?.updatedAt.toLocaleString("id-ID", {
                    dateStyle: 'medium',
                    timeStyle: 'short'
                  })}
                </div>
              </div>
              <div>
                <label className="block text-gray-500 font-medium mb-1">Deskripsi</label>
                <p className="text-gray-600 italic leading-relaxed">
                  {tableDetail?.description || 'Tidak ada deskripsi'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-xl shadow-gray-100 border border-gray-100">
            <h3 className="text-lg font-bold flex items-center gap-2 mb-4">
              <TagIcon className="size-5 text-purple-600" />
              Metadata Tabel
            </h3>
            {metadata.length === 0 ? (
              <p className="text-sm text-gray-400 italic text-center py-4">Belum ada metadata</p>
            ) : (
              <div className="space-y-3">
                {metadata.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-start gap-4 p-2 hover:bg-gray-50 rounded-lg transition-colors">
                    <span className="text-sm font-bold text-gray-700">{item.name}</span>
                    <span className="text-sm text-gray-600 text-right">{item.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column: API Reference & Sample Data */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
            <div className="bg-gray-800 px-6 py-3 flex items-center justify-between border-b border-gray-700">
              <div className="flex items-center gap-2">
                <CodeBracketIcon className="size-5 text-blue-400" />
                <span className="text-xs font-bold text-gray-300 uppercase tracking-widest">HTTP Request</span>
              </div>
              <div className="flex gap-1.5">
                <div className="size-3 rounded-full bg-red-500"></div>
                <div className="size-3 rounded-full bg-yellow-500"></div>
                <div className="size-3 rounded-full bg-green-500"></div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 bg-blue-600 text-white rounded text-xs font-bold">POST</span>
                <span className="text-blue-400 font-mono text-sm break-all">{`${process.env.REACT_APP_BACKEND_URL}/api/table-content/read`}</span>
              </div>
              
              <div className="space-y-2">
                <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">Request Body</div>
                <pre className="bg-gray-950 p-4 rounded-xl text-blue-300 text-sm overflow-x-auto border border-gray-800">
{`{
  "tableId": "${tableDetail?.id}",
  "format": "simple" // (optional: "simple" or default)
}`}
                </pre>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Default Response</div>
                  <pre className="bg-gray-950 p-4 rounded-xl text-green-400 text-xs overflow-x-auto border border-gray-800 max-h-60 overflow-y-auto">
{`{
  "status": 200,
  "success": true,
  "data": {
    "tableName": "${tableDetail?.name}",
    "rows": [
      {
        "rowId": "UUID",
        "values": [
          {
            "columnName": "Nama Kolom",
            "value": "Nilai"
          }
        ]
      }
    ]
  },
  "meta": [
    {
      "name": "Metadata Name",
      "value": "Metadata Value"
    }
  ]
}`}
                  </pre>
                </div>
                <div>
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Simple Response</div>
                  <pre className="bg-gray-950 p-4 rounded-xl text-yellow-400 text-xs overflow-x-auto border border-gray-800 max-h-60 overflow-y-auto">
{`{
  "status": 200,
  "success": true,
  "data": [
    {
      "rowId": "UUID",
      "Kolom A": "Nilai A",
      "Kolom B": 123
    }
  ],
  "meta": [
    {
      "name": "Metadata Name",
      "value": "Metadata Value"
    }
  ]
}`}
                  </pre>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl shadow-gray-100 border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-900">Preview Data</h3>
              <span className="text-xs font-medium text-gray-500 bg-gray-200 px-2 py-1 rounded-md">Live Preview</span>
            </div>
            <div className="overflow-x-auto max-h-[400px]">
              <table className="w-full text-left border-collapse">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="bg-gray-50">
                    {columns.map((col, idx) => (
                      <th key={idx} className="px-6 py-3 text-xs font-bold text-gray-600 uppercase tracking-wider border-b">
                        {col.columnName}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="px-6 py-10 text-center text-gray-400 italic">
                        Tidak ada data untuk dipratinjau
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, rIdx) => (
                      <tr key={rIdx} className="hover:bg-blue-50/30 transition-colors">
                        {columns.map((col, cIdx) => {
                          const cell = row.values.find(v => 
                            v.columnId ? v.columnId === col.columnId : v.columnShownAtIndex === col.columnShownAtIndex
                          )
                          return (
                            <td key={cIdx} className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                              {cell?.value || '-'}
                            </td>
                          )
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
